import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  IconAlertTriangle,
  IconCheck,
  IconClipboard,
  IconLoader2,
  IconPlus,
  IconRefresh,
  IconRotate,
  IconSelector,
  IconTable,
} from "@tabler/icons-react";
import {
  searchEncoderCodesQuery,
  useEncoderCatalog,
  useEncoderFhirPrefill,
  useEncoderMsdrgElixhauser,
  useEncoderMsdrgGroup,
  useEncoderMsdrgMceEdit,
  useEncoderMsdrgOptions,
  useEncoderMsdrgSexRestrictions,
} from "../../hooks/queries";
import type {
  CodeSearchResult,
  EncoderElixhauserResult,
  EncoderLabeledOption,
  MedicareCodeEditorRequest,
  MedicareCodeEditorResponse,
  MsDrgGroupResponse,
  MsDrgSex,
} from "../../types/encoder";
import {
  cn,
  currentQuarter,
  daysBetween,
  DEFAULT_DISCHARGE_DISPOSITIONS,
  DEFAULT_POA_OPTIONS,
  dischargeStatusCode,
  elixhauserDispositionCode,
  encoderErrorMessage,
  formatEncoderProfileDate,
  getDefaultEncoderMsDrgProfiles,
  isIcd10DxLike,
  isPcsLike,
  newScratchId,
  parsePastedCodes,
  type EncoderMsDrgProfile,
} from "../../utils/encoder";
import {
  formatEncoderGrouperVersion,
  getEncoderHacAlerts,
  medicareCodeEditAlerts,
} from "../../utils/encoder-msdrg";
import { useEncoderUi } from "./context";
import CmBuilderTab from "./CmBuilderTab";
import PcsBuilderTab from "./PcsBuilderTab";
import {
  CodeLabels,
  EncoderDialog,
  MergedCodeSearch,
  NativeSelect,
} from "./shared";

type SessionProfile = EncoderMsDrgProfile & {
  isDefault?: boolean;
  isTemporary?: boolean;
};

type GrouperCode = {
  id: string;
  code: string;
  description: string;
  type: string;
  poa: string;
  confirmed: boolean;
  labels: string[];
  metadata?: CodeSearchResult["metadata"];
  sexRestriction?: string;
};

type DrgOptionRow = {
  pdx: string;
  pdxDescription: string;
  ppx: string;
  drg: string;
  weight: string;
  alos: string;
  olos: string;
  reimb: string;
  drgDescription: string;
  drgType: string;
  mcEdits: "passed" | "alert" | "failed";
  hac: "passed" | "alert";
  elix: string;
};

const formatDrgReimb = (value?: string): string => {
  const text = (value || "").trim();
  if (!text) return "—";
  if (text.indexOf("$") >= 0) return text;
  const amount = Number(text.replace(/,/g, ""));
  if (!Number.isFinite(amount)) return text;
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

const PassedChip: React.FC<{ label?: string }> = ({ label = "PASSED" }) => (
  <span className="inline-flex items-center gap-0.5 rounded-full bg-green-50 px-1.5 py-0.5 text-[10px] font-semibold text-green-700">
    {label}
    <IconCheck size={12} stroke={3} />
  </span>
);

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const StatusPill: React.FC<{
  tone: "green" | "red" | "blue" | "amber";
  children: React.ReactNode;
}> = ({ tone, children }) => {
  const color =
    tone === "green"
      ? "text-green-700"
      : tone === "red"
        ? "text-red-700"
        : tone === "amber"
          ? "text-amber-700"
          : "text-blue-700";
  return (
    <span
      className={`ml-auto mr-1 flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-white px-2 py-0.5 text-sm font-bold ${color}`}
    >
      {children}
    </span>
  );
};

const getRestrictedSex = (restriction?: string): MsDrgSex | null => {
  const value = (restriction || "").toUpperCase();
  if (value.includes("FEMALE")) return "FEMALE";
  if (value.includes("MALE")) return "MALE";
  return null;
};

const hasSexRestrictionMismatch = (
  restriction: string | undefined,
  sex: MsDrgSex
): boolean => {
  const restricted = getRestrictedSex(restriction);
  return Boolean(restricted && sex !== "UNKNOWN" && restricted !== sex);
};

const fromSearch = (result: CodeSearchResult, poa = "Y"): GrouperCode => ({
  id: newScratchId(),
  code: result.code,
  description: result.description,
  type: result.type === "PCS" || isPcsLike(result.code) ? "PCS" : "Dx",
  poa,
  confirmed: true,
  labels: result.labels ?? [],
  metadata: result.metadata,
});

const dispositionLabel = (
  dispositions: EncoderLabeledOption[],
  status: string
): string => {
  const match = dispositions.find((item) => {
    const code = (item.code || item.value || "").replace(/^0+/, "") || "0";
    const padded = (item.code || item.value || "").padStart(2, "0");
    return (
      item.value === status ||
      item.code === status ||
      padded === status.padStart(2, "0") ||
      code === String(Number.parseInt(status, 10) || status)
    );
  });
  return match?.label || status;
};

const sexLabel = (sex: MsDrgSex): string =>
  sex === "MALE" ? "Male" : sex === "FEMALE" ? "Female" : "Unknown";

const MsDrgGrouperTab: React.FC = () => {
  const catalog = useEncoderCatalog();
  const optionsQuery = useEncoderMsdrgOptions();
  const { showToast } = useEncoderUi();
  const poaOptions: EncoderLabeledOption[] =
    catalog.data?.poaOptions?.length
      ? catalog.data.poaOptions
      : Array.isArray(asRecord(optionsQuery.data)?.poa)
        ? (asRecord(optionsQuery.data)?.poa as EncoderLabeledOption[])
        : DEFAULT_POA_OPTIONS;
  const sexOptions: EncoderLabeledOption[] = catalog.data?.sexOptions?.length
    ? catalog.data.sexOptions
    : [
        { value: "MALE", label: "Male" },
        { value: "FEMALE", label: "Female" },
        { value: "UNKNOWN", label: "Unknown" },
      ];
  const dispositions: EncoderLabeledOption[] = catalog.data
    ?.dischargeDispositions?.length
    ? catalog.data.dischargeDispositions
    : DEFAULT_DISCHARGE_DISPOSITIONS;
  const years: number[] = catalog.data?.codeYears?.length
    ? catalog.data.codeYears
    : [2026, 2025, 2024, 2023, 2022];

  const defaultProfiles = useMemo(
    () =>
      getDefaultEncoderMsDrgProfiles().map((item, index) => ({
        ...item,
        isDefault: true,
        isTemporary: false,
        name: item.name || (index === 0 ? "Male Patient" : "Female Patient"),
      })),
    []
  );
  const [profiles, setProfiles] = useState<SessionProfile[]>(defaultProfiles);
  const [profileId, setProfileId] = useState(defaultProfiles[0]?.id ?? "");
  const [profile, setProfile] = useState<SessionProfile>(defaultProfiles[0]);
  const [year, setYear] = useState(years[0] ?? 2026);
  const [codeType, setCodeType] = useState("All");
  const [selected, setSelected] = useState<CodeSearchResult | null>(null);
  const [codes, setCodes] = useState<GrouperCode[]>([]);
  const [principal, setPrincipal] = useState<string | null>(null);
  const [dxOpen, setDxOpen] = useState(false);
  const [pcsOpen, setPcsOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [draftPdx, setDraftPdx] = useState<string | null>(null);
  const [drgOptionRows, setDrgOptionRows] = useState<DrgOptionRow[]>([]);
  const [drgOptionsLoading, setDrgOptionsLoading] = useState(false);
  const [profilePickerOpen, setProfilePickerOpen] = useState(false);
  const [profileDialog, setProfileDialog] = useState<"create" | "temporary" | null>(
    null
  );
  const [draftName, setDraftName] = useState("");
  const [draftProfile, setDraftProfile] = useState<SessionProfile>(defaultProfiles[0]);
  const [prefillApplied, setPrefillApplied] = useState(false);
  const [groupResult, setGroupResult] = useState<MsDrgGroupResponse | null>(
    null
  );
  const [elixhauser, setElixhauser] = useState<EncoderElixhauserResult | null>(
    null
  );
  const [mce, setMce] = useState<MedicareCodeEditorResponse | null>(null);
  const [mceFailed, setMceFailed] = useState(false);
  const [mceError, setMceError] = useState("");
  const [elixhauserFailed, setElixhauserFailed] = useState(false);
  const [elixhauserError, setElixhauserError] = useState("");
  const [hasNoElixhauserDiagnoses, setHasNoElixhauserDiagnoses] =
    useState(false);
  const lastGrouperSignature = useRef<string | null>(null);
  const lastMceInput = useRef<MedicareCodeEditorRequest | null>(null);
  const lastElixInput = useRef<Record<string, unknown> | null>(null);
  const profilePickerRef = useRef<HTMLDivElement>(null);
  const group = useEncoderMsdrgGroup();
  const sexRestrictions = useEncoderMsdrgSexRestrictions();
  const elix = useEncoderMsdrgElixhauser();
  const mceEdit = useEncoderMsdrgMceEdit();
  const prefill = useEncoderFhirPrefill();

  useEffect(() => {
    const next = profiles.find((item) => item.id === profileId);
    if (next) setProfile(next);
  }, [profileId, profiles]);

  useEffect(() => {
    if (!profilePickerOpen) return undefined;
    const onClick = (event: MouseEvent) => {
      if (
        profilePickerRef.current &&
        !profilePickerRef.current.contains(event.target as Node)
      ) {
        setProfilePickerOpen(false);
      }
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [profilePickerOpen]);

  useEffect(() => {
    if (prefillApplied || !prefill.data) return;
    const data = prefill.data;
    setProfile((current) => ({
      ...current,
      sex: data.sex || current.sex,
      ageAtAdmission: data.ageInYears ?? current.ageAtAdmission,
      admitDate: data.admitDate || current.admitDate,
      dischargeDate: data.dischargeDate || current.dischargeDate,
      dischargeStatus: data.dischargeStatus
        ? String(data.dischargeStatus).padStart(2, "0")
        : current.dischargeStatus,
    }));
    setPrefillApplied(true);
  }, [prefill.data, prefillApplied]);

  const diagnosisCodes = codes.filter((code) => code.type !== "PCS");
  const procedureCodes = codes.filter((code) => code.type === "PCS");
  const mismatchedCodeSex = useMemo(() => {
    if (profile.sex === "UNKNOWN") return null;
    const mismatch = codes.find(
      (code) =>
        code.confirmed && hasSexRestrictionMismatch(code.sexRestriction, profile.sex)
    );
    return mismatch ? getRestrictedSex(mismatch.sexRestriction) : null;
  }, [codes, profile.sex]);

  const addCode = (result: CodeSearchResult) => {
    const next = fromSearch(result);
    const mismatch = hasSexRestrictionMismatch(next.sexRestriction, profile.sex);
    const row = { ...next, confirmed: !mismatch };
    setCodes((current) => [...current, row]);
    if (!principal && row.type !== "PCS" && !mismatch) setPrincipal(row.code);
    setSelected(null);
    setGroupResult(null);
  };

  const pasteCodes = async () => {
    let text = "";
    try {
      text = await navigator.clipboard.readText();
    } catch {
      showToast("Unable to read the clipboard", "error");
      return;
    }
    const tokens = parsePastedCodes(text);
    if (!tokens.length) {
      showToast("No codes found on the clipboard", "info");
      return;
    }
    const searches = await Promise.all(
      tokens.slice(0, 25).map(async (token) => {
        const type = isPcsLike(token) ? "PCS" : "Dx";
        const results = await searchEncoderCodesQuery({
          q: token,
          type,
          year,
          limit: 5,
        });
        return (
          results.find(
            (item) =>
              item.code.replace(/\./g, "").toUpperCase() ===
              token.replace(/\./g, "")
          ) ?? results[0]
        );
      })
    );
    const found = searches.filter(Boolean) as CodeSearchResult[];
    found.forEach(addCode);
    showToast(`Added ${found.length} pasted code(s)`, "success");
  };

  const runGroup = async (silent = false) => {
    const confirmedDx = diagnosisCodes.filter(
      (code) => code.confirmed && isIcd10DxLike(code.code)
    );
    const confirmedPcs = procedureCodes.filter(
      (code) => code.confirmed && isPcsLike(code.code)
    );
    const principalRow = confirmedDx.find((code) => code.code === principal);
    if (!principalRow) {
      if (!silent) showToast("Select a principal diagnosis", "error");
      return;
    }
    const admit = new Date(`${profile.admitDate}T00:00:00`);
    const discharge = new Date(`${profile.dischargeDate}T00:00:00`);
    const los =
      Number.isNaN(admit.getTime()) || Number.isNaN(discharge.getTime())
        ? 0
        : daysBetween(admit, discharge);
    // CMS grouper only uses age-in-days for neonates. Adult age-in-days-of-life
    // (e.g. 65 * 365) is rejected by the V2 API with 502.
    const isNeonate = profile.ageAtAdmission < 1;
    const ageDaysAdmit = isNeonate
      ? Math.max(0, Math.round(profile.ageAtAdmission * 365))
      : 0;
    const ageDaysDischarge = isNeonate ? ageDaysAdmit + los : los;
    const grouperVersionDate = /^\d{4}-\d{2}-\d{2}$/.test(profile.admitDate)
      ? profile.admitDate
      : undefined;
    const dischargeStatus = dischargeStatusCode(
      dispositions.find(
        (item) =>
          item.code === profile.dischargeStatus ||
          item.value === profile.dischargeStatus
      ),
      profile.dischargeStatus
    );
    try {
      const undot = (code: string) => code.replace(/\./g, "");
      const secondaryDx = confirmedDx.filter(
        (code) => code.code !== principalRow.code
      );
      const hasSecondaryDx = secondaryDx.length > 0;
      const mceInput: MedicareCodeEditorRequest = {
        dischargeDate: profile.dischargeDate,
        admissionDiagnosisCode: undot(principalRow.code),
        principalDiagnosisCode: undot(principalRow.code),
        secondaryDiagnosisCodes: secondaryDx.map((code) => undot(code.code)),
        procedureCodes: confirmedPcs.map((code) => undot(code.code)),
        ageYears: profile.ageAtAdmission,
        sex: profile.sex === "MALE" ? 1 : profile.sex === "FEMALE" ? 2 : 0,
        dischargeStatus: Number.parseInt(dischargeStatus, 10) || 0,
        lengthOfStay: los,
      };
      lastMceInput.current = mceInput;
      const elixInput = hasSecondaryDx
        ? {
            claim_id: "encoder-ms-drg-grouper",
            patient_id: `encoder-${profile.id}`,
            age: profile.ageAtAdmission,
            principal_diagnosis: undot(principalRow.code),
            discharge_disposition: {
              code: elixhauserDispositionCode(dischargeStatus),
              system: "HCUP_DISPUNIFORM",
              version: "2026",
            },
            length_of_stay: los,
            secondary_diagnoses: secondaryDx.map((code) => ({
              code: undot(code.code),
              poa: code.poa,
            })),
            year: Number(profile.dischargeDate.slice(0, 4)) || 2026,
            quarter: currentQuarter(discharge),
          }
        : null;
      lastElixInput.current = elixInput;
      setHasNoElixhauserDiagnoses(!hasSecondaryDx);
      setMceFailed(false);
      setMceError("");
      setElixhauserFailed(false);
      setElixhauserError("");
      const [groupOutcome, sexOutcome, elixOutcome, mceOutcome] =
        await Promise.allSettled([
        group.mutateAsync({
          principalDiagnosisCode: {
            value: principalRow.code,
            poa: principalRow.poa,
          },
          admissionDiagnosisCode: {
            value: principalRow.code,
            poa: principalRow.poa,
          },
          ageInYears: profile.ageAtAdmission,
          ageDaysAdmit,
          ageDaysDischarge: ageDaysAdmit + los,
          sex: profile.sex,
          dischargeStatus,
          secondaryDiagnosisCodes: secondaryDx.map((code) => ({
            value: code.code,
            poa: code.poa,
          })),
          procedureCodes: confirmedPcs.map((code) => ({ value: code.code })),
          ...(grouperVersionDate ? { grouperVersionDate } : {}),
        }),
        sexRestrictions.mutateAsync({
          diagnosisCodes: confirmedDx.map((code) => code.code),
          procedureCodes: confirmedPcs.map((code) => code.code),
        }),
        elixInput ? elix.mutateAsync(elixInput) : Promise.resolve(null),
        mceEdit.mutateAsync(mceInput),
      ]);
      if (groupOutcome.status === "rejected") throw groupOutcome.reason;
      setGroupResult(groupOutcome.value);
      if (sexOutcome.status === "fulfilled") {
        const restrictionRows = (value: unknown): Array<{ code: string; restriction: string }> =>
          (Array.isArray(value) ? value : [])
            .map((item) => asRecord(item))
            .filter((item): item is Record<string, unknown> => Boolean(item))
            .map((item) => ({
              code: String(item.code ?? ""),
              restriction: String(item.restriction ?? ""),
            }))
            .filter((item) => item.code);
        const dxRestrictions = new Map(
          restrictionRows(sexOutcome.value.diagnosisCodes).map((item) => [
            item.code,
            item.restriction,
          ])
        );
        const pcsRestrictions = new Map(
          restrictionRows(sexOutcome.value.procedureCodes).map((item) => [
            item.code,
            item.restriction,
          ])
        );
        setCodes((current) =>
          current.map((code) => ({
            ...code,
            sexRestriction:
              dxRestrictions.get(code.code) ?? pcsRestrictions.get(code.code),
          }))
        );
      }
      if (elixOutcome.status === "fulfilled") {
        setElixhauser(elixOutcome.value);
        setElixhauserFailed(false);
        setElixhauserError("");
      } else {
        setElixhauser(null);
        setElixhauserFailed(hasSecondaryDx);
        setElixhauserError(
          hasSecondaryDx
            ? encoderErrorMessage(
                elixOutcome.reason,
                "Elixhauser analysis could not be completed"
              )
            : ""
        );
      }
      if (mceOutcome.status === "fulfilled") {
        setMce(mceOutcome.value);
        setMceFailed(mceOutcome.value.success === false);
        setMceError(
          mceOutcome.value.success === false
            ? mceOutcome.value.error || "Medicare Code Editor validation failed."
            : ""
        );
      } else {
        setMce(null);
        setMceFailed(true);
        setMceError(
          encoderErrorMessage(
            mceOutcome.reason,
            "Medicare Code Editor validation failed."
          )
        );
      }
      if (!silent) showToast("MS-DRG grouping complete", "success");
    } catch (error) {
      showToast(
        `Encoder failed: ${encoderErrorMessage(error, "Grouping failed")}`,
        "error"
      );
    }
  };

  const previewDrgOption = async (
    pdxRow: GrouperCode,
    confirmedDx: GrouperCode[],
    confirmedPcs: GrouperCode[],
    ppx: string
  ): Promise<DrgOptionRow> => {
    const admit = new Date(`${profile.admitDate}T00:00:00`);
    const discharge = new Date(`${profile.dischargeDate}T00:00:00`);
    const los =
      Number.isNaN(admit.getTime()) || Number.isNaN(discharge.getTime())
        ? 0
        : daysBetween(admit, discharge);
    const isNeonate = profile.ageAtAdmission < 1;
    const ageDaysAdmit = isNeonate
      ? Math.max(0, Math.round(profile.ageAtAdmission * 365))
      : 0;
    const grouperVersionDate = /^\d{4}-\d{2}-\d{2}$/.test(profile.admitDate)
      ? profile.admitDate
      : undefined;
    const dischargeStatus = dischargeStatusCode(
      dispositions.find(
        (item) =>
          item.code === profile.dischargeStatus ||
          item.value === profile.dischargeStatus
      ),
      profile.dischargeStatus
    );
    const undot = (code: string) => code.replace(/\./g, "");
    const secondaryDx = confirmedDx.filter(
      (code) => code.code !== pdxRow.code
    );
    const fallback: DrgOptionRow = {
      pdx: pdxRow.code,
      pdxDescription: pdxRow.description,
      ppx,
      drg: "—",
      weight: "—",
      alos: "—",
      olos: "—",
      reimb: "—",
      drgDescription: "",
      drgType: "",
      mcEdits: "failed",
      hac: "passed",
      elix: secondaryDx.length ? "—" : "No secondary Dx",
    };
    try {
      const elixInput = secondaryDx.length
        ? {
            claim_id: "encoder-ms-drg-options",
            patient_id: `encoder-${profile.id}`,
            age: profile.ageAtAdmission,
            principal_diagnosis: undot(pdxRow.code),
            discharge_disposition: {
              code: elixhauserDispositionCode(dischargeStatus),
              system: "HCUP_DISPUNIFORM",
              version: "2026",
            },
            length_of_stay: los,
            secondary_diagnoses: secondaryDx.map((code) => ({
              code: undot(code.code),
              poa: code.poa,
            })),
            year: Number(profile.dischargeDate.slice(0, 4)) || 2026,
            quarter: currentQuarter(discharge),
          }
        : null;
      const [groupOutcome, elixOutcome, mceOutcome] = await Promise.allSettled([
        group.mutateAsync({
          principalDiagnosisCode: {
            value: pdxRow.code,
            poa: pdxRow.poa,
          },
          admissionDiagnosisCode: {
            value: pdxRow.code,
            poa: pdxRow.poa,
          },
          ageInYears: profile.ageAtAdmission,
          ageDaysAdmit,
          ageDaysDischarge: ageDaysAdmit + los,
          sex: profile.sex,
          dischargeStatus,
          secondaryDiagnosisCodes: secondaryDx.map((code) => ({
            value: code.code,
            poa: code.poa,
          })),
          procedureCodes: confirmedPcs.map((code) => ({ value: code.code })),
          ...(grouperVersionDate ? { grouperVersionDate } : {}),
        }),
        elixInput ? elix.mutateAsync(elixInput) : Promise.resolve(null),
        mceEdit.mutateAsync({
          dischargeDate: profile.dischargeDate,
          admissionDiagnosisCode: undot(pdxRow.code),
          principalDiagnosisCode: undot(pdxRow.code),
          secondaryDiagnosisCodes: secondaryDx.map((code) => undot(code.code)),
          procedureCodes: confirmedPcs.map((code) => undot(code.code)),
          ageYears: profile.ageAtAdmission,
          sex: profile.sex === "MALE" ? 1 : profile.sex === "FEMALE" ? 2 : 0,
          dischargeStatus: Number.parseInt(dischargeStatus, 10) || 0,
          lengthOfStay: los,
        }),
      ]);
      if (groupOutcome.status !== "fulfilled") return fallback;
      const result = groupOutcome.value;
      const details = result.details;
      const hac = getEncoderHacAlerts(result.grouperDetails);
      const mceValue =
        mceOutcome.status === "fulfilled" ? mceOutcome.value : null;
      const mceHasAlerts = mceValue
        ? medicareCodeEditAlerts(mceValue).length > 0
        : false;
      const elixValue =
        elixOutcome.status === "fulfilled" ? elixOutcome.value : null;
      const triggered = elixValue?.triggered_measures?.length ?? 0;
      return {
        pdx: pdxRow.code,
        pdxDescription: pdxRow.description,
        ppx,
        drg: result.value != null ? String(result.value) : "—",
        weight: details?.Weights ? String(details.Weights) : "—",
        alos: details?.ALOS ? String(details.ALOS) : "—",
        olos: details?.GLOS ? String(details.GLOS) : "—",
        reimb: formatDrgReimb(details?.DOLLARS),
        drgDescription: String(
          details?.["MS-DRG Title"] || result.description || ""
        ),
        drgType: details?.TYPE ? String(details.TYPE) : "",
        mcEdits:
          mceOutcome.status !== "fulfilled" || mceValue?.success === false
            ? "failed"
            : mceHasAlerts
              ? "alert"
              : "passed",
        hac: hac.length > 0 ? "alert" : "passed",
        elix: secondaryDx.length
          ? triggered > 0
            ? `${triggered} comorbidit${triggered === 1 ? "y" : "ies"}`
            : "PASSED"
          : "No secondary Dx",
      };
    } catch {
      return fallback;
    }
  };

  const openDrgOptions = async () => {
    const confirmedDx = diagnosisCodes.filter(
      (code) => code.confirmed && isIcd10DxLike(code.code)
    );
    const confirmedPcs = procedureCodes.filter(
      (code) => code.confirmed && isPcsLike(code.code)
    );
    setDraftPdx(principal);
    setDetailsOpen(true);
    if (!confirmedDx.length) {
      setDrgOptionRows([]);
      return;
    }
    setDrgOptionsLoading(true);
    try {
      const ppx = confirmedPcs[0]?.code || "—";
      const rows = await Promise.all(
        confirmedDx.map((row) =>
          previewDrgOption(row, confirmedDx, confirmedPcs, ppx)
        )
      );
      setDrgOptionRows(rows);
    } finally {
      setDrgOptionsLoading(false);
    }
  };

  const applyDrgOption = () => {
    if (draftPdx) setPrincipal(draftPdx);
    setDetailsOpen(false);
  };

  const grouperSignature = useMemo(
    () =>
      JSON.stringify({
        principal,
        dx: diagnosisCodes
          .filter((code) => code.confirmed)
          .map((code) => `${code.code}:${code.poa}`),
        pcs: procedureCodes
          .filter((code) => code.confirmed)
          .map((code) => code.code),
        profile: {
          sex: profile.sex,
          age: profile.ageAtAdmission,
          admit: profile.admitDate,
          discharge: profile.dischargeDate,
          status: profile.dischargeStatus,
        },
      }),
    [
      diagnosisCodes,
      principal,
      procedureCodes,
      profile.admitDate,
      profile.ageAtAdmission,
      profile.dischargeDate,
      profile.dischargeStatus,
      profile.sex,
    ]
  );

  useEffect(() => {
    const confirmedDx = diagnosisCodes.filter((code) => code.confirmed);
    const hasPrincipal = Boolean(
      principal && confirmedDx.some((code) => code.code === principal)
    );
    if (!confirmedDx.length || !hasPrincipal) {
      setGroupResult(null);
      setMce(null);
      setElixhauser(null);
      setMceFailed(false);
      setElixhauserFailed(false);
      setHasNoElixhauserDiagnoses(false);
      lastGrouperSignature.current = null;
      return;
    }
    if (lastGrouperSignature.current === grouperSignature) return;
    lastGrouperSignature.current = grouperSignature;
    void runGroup(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouperSignature]);

  const details = groupResult?.details ?? null;
  const grouperDetails = groupResult?.grouperDetails ?? null;
  const hacAlerts = getEncoderHacAlerts(grouperDetails);
  const grouperVersion = formatEncoderGrouperVersion(
    asRecord(grouperDetails)?.grouperVersion
  );
  const hacStatus = String(asRecord(grouperDetails)?.hacStatus ?? "");
  const mceAlerts = medicareCodeEditAlerts(mce);

  const retryMce = async () => {
    if (!lastMceInput.current) return;
    setMceFailed(false);
    setMceError("");
    try {
      const result = await mceEdit.mutateAsync(lastMceInput.current);
      setMce(result);
      setMceFailed(result.success === false);
      setMceError(
        result.success === false
          ? result.error || "Medicare Code Editor validation failed."
          : ""
      );
    } catch (error) {
      setMceFailed(true);
      setMceError(
        encoderErrorMessage(error, "Medicare Code Editor validation failed.")
      );
    }
  };

  const retryElixhauser = async () => {
    if (!lastElixInput.current) return;
    setElixhauserFailed(false);
    setElixhauserError("");
    try {
      const result = await elix.mutateAsync(lastElixInput.current);
      setElixhauser(result);
      setElixhauserFailed(false);
    } catch (error) {
      setElixhauser(null);
      setElixhauserFailed(true);
      setElixhauserError(
        encoderErrorMessage(error, "Elixhauser analysis could not be completed")
      );
    }
  };

  const openProfileDialog = (mode: "create" | "temporary") => {
    setProfilePickerOpen(false);
    setProfileDialog(mode);
    setDraftName(
      mode === "temporary"
        ? `Temporary profile ${profiles.filter((item) => item.isTemporary).length + 1}`
        : ""
    );
    setDraftProfile({
      ...profile,
      id: newScratchId(),
      name: "",
      isDefault: false,
      isTemporary: mode === "temporary",
    });
  };

  const saveProfile = () => {
    if (!draftName.trim()) {
      showToast("Enter a profile name", "error");
      return;
    }
    if (draftProfile.admitDate > draftProfile.dischargeDate) {
      showToast("Discharge date must be on or after admit date", "error");
      return;
    }
    const next: SessionProfile = {
      ...draftProfile,
      name: draftName.trim(),
      isDefault: false,
      isTemporary: profileDialog === "temporary",
    };
    setProfiles((current) => [...current, next]);
    setProfileId(next.id);
    setProfile(next);
    setProfileDialog(null);
    showToast(
      next.isTemporary ? "Temporary profile applied" : "Profile created",
      "success"
    );
  };

  const renderCodeTable = (
    title: "ICD-10" | "PCS",
    items: GrouperCode[],
    pcs: boolean
  ) => (
    <div className="bg-white">
      <div
        className={`flex min-h-9 border-b border-gray-200 px-2 py-1 ${
          pcs ? "bg-orange-100" : "bg-fuchsia-100"
        }`}
      >
        <div className="grid flex-1 grid-cols-[30px_75px_minmax(0,1fr)_60px_50px_70px] items-center gap-2 text-sm font-semibold text-gray-700">
          <div className="text-center">#</div>
          <div>{title}</div>
          <div>DESCRIPTION</div>
          {!pcs ? (
            <>
              <div className="text-center">POA</div>
              <div className="text-center">PDx</div>
            </>
          ) : (
            <>
              <div aria-hidden="true" />
              <div aria-hidden="true" />
            </>
          )}
          <div className="text-center">CONFIRM</div>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="border-x border-b border-gray-200 p-4 text-sm text-slate-500">
          Add {pcs ? "procedure" : "diagnosis"} codes to begin.
        </div>
      ) : (
        items.map((code, index) => (
          <div
            key={code.id}
            className={`group relative grid min-h-12 grid-cols-[30px_75px_minmax(0,1fr)_60px_50px_70px] items-start gap-2 border-b px-2 py-1 text-sm ${
              index % 2 === 0 ? "bg-gray-50" : "bg-white"
            }`}
          >
            <div className="flex flex-col items-center pt-1">{index + 1}</div>
            <div className="min-w-0 font-mono">{code.code}</div>
            <div className="min-w-0">
              <p className="line-clamp-2">{code.description}</p>
              <div className="mt-0.5 flex flex-wrap items-center gap-1">
                <CodeLabels result={code} colors={catalog.data?.labelColors} />
                {getRestrictedSex(code.sexRestriction) ? (
                  <span
                    className={`w-12 rounded-sm border p-px text-center text-[10px] font-medium ${
                      getRestrictedSex(code.sexRestriction) === "FEMALE"
                        ? "border-pink-300 bg-pink-100 text-pink-700"
                        : "border-blue-300 bg-blue-100 text-blue-700"
                    }`}
                  >
                    {getRestrictedSex(code.sexRestriction) === "FEMALE"
                      ? "Female"
                      : "Male"}
                  </span>
                ) : null}
              </div>
            </div>
            {!pcs ? (
              <>
                <NativeSelect
                  value={code.poa}
                  onChange={(value) =>
                    setCodes((current) =>
                      current.map((item) =>
                        item.id === code.id ? { ...item, poa: value } : item
                      )
                    )
                  }
                  className="h-7 w-full px-1 text-xs"
                  options={(poaOptions.length ? poaOptions : DEFAULT_POA_OPTIONS).map(
                    (option) => ({
                      value: option.value,
                      label: option.value,
                    })
                  )}
                />
                <div className="flex items-center justify-center pt-1">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-blue-600"
                    checked={principal === code.code}
                    onChange={() => setPrincipal(code.code)}
                    aria-label={`Set ${code.code} as principal diagnosis`}
                  />
                </div>
              </>
            ) : (
              <>
                <div aria-hidden="true" />
                <div aria-hidden="true" />
              </>
            )}
            <div className="flex items-center justify-center pt-1">
              <input
                type="checkbox"
                className="h-4 w-4 accent-green-600"
                checked={code.confirmed}
                onChange={(event) =>
                  setCodes((current) =>
                    current.map((item) =>
                      item.id === code.id
                        ? { ...item, confirmed: event.target.checked }
                        : item
                    )
                  )
                }
                aria-label={`Confirm ${code.code}`}
              />
            </div>
          </div>
        ))
      )}
    </div>
  );

  const drgValue = groupResult ? String(groupResult.value ?? "") : "";
  const drgDescription = String(
    details?.["MS-DRG Title"] || groupResult?.description || ""
  );
  const mdc = details?.MDC ? String(details.MDC) : "";
  const mdcDescription = details?.MDCDescription
    ? String(details.MDCDescription)
    : "";
  const drgType = details?.TYPE ? String(details.TYPE) : "";
  const triggeredMeasures = elixhauser?.triggered_measures ?? [];
  const reviewCount =
    (elixhauser?.suppressed_measures?.length ?? 0) +
    (elixhauser?.rejected_measures?.length ?? 0);
  const hasElixhauserFindings =
    triggeredMeasures.length > 0 || reviewCount > 0;
  const mceChecking = mceEdit.isPending;
  const mceHasAlerts = mceAlerts.length > 0;
  const hacStatusMessage =
    hacStatus === "FINAL_DRG_NO_CHANGE"
      ? "HAC criteria were met, but the final DRG did not change."
      : hacStatus === "FINAL_DRG_CHANGES"
        ? "HAC criteria were met and changed the final DRG."
        : hacStatus === "FINAL_DRG_UNGROUPABLE"
          ? "HAC processing resulted in an ungroupable final DRG."
          : "";

  return (
    <section className="flex min-h-0 w-full flex-1 flex-col">
      <div className="sticky top-0 z-20 border-b bg-white p-2 shadow-sm">
        <div className="flex w-full items-center gap-4">
          <div className="flex w-3/5 min-w-0 items-center gap-2">
            <MergedCodeSearch
              year={year}
              years={years}
              type={codeType}
              types={["All", "Dx", "PCS"]}
              onYearChange={setYear}
              onTypeChange={setCodeType}
              onSelect={setSelected}
              selected={selected}
            />
            <button
              type="button"
              className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md bg-slate-200 px-3 text-sm font-medium text-slate-800 disabled:opacity-50"
              onClick={() => selected && addCode(selected)}
              disabled={!selected}
            >
              <IconPlus size={14} /> Add Code
            </button>
            <button
              type="button"
              className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md border border-fuchsia-200 bg-fuchsia-100 px-3 text-sm text-gray-700 hover:bg-fuchsia-200"
              onClick={() => void pasteCodes()}
            >
              <IconClipboard size={14} /> Paste Codes
            </button>
            <button
              type="button"
              className="inline-flex h-9 shrink-0 items-center rounded-md border border-orange-200 bg-orange-100 px-3 text-sm text-gray-700 hover:bg-orange-200"
              onClick={() => setPcsOpen(true)}
            >
              PCS Builder
            </button>
            <button
              type="button"
              className="inline-flex h-9 shrink-0 items-center rounded-md border border-fuchsia-200 bg-fuchsia-100 px-3 text-sm text-gray-700 hover:bg-fuchsia-200"
              onClick={() => setDxOpen(true)}
            >
              Dx Builder
            </button>
            <button
              type="button"
              className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md bg-slate-900 px-3 text-sm text-white"
              onClick={() => {
                setCodes([]);
                setPrincipal(null);
                setSelected(null);
                setGroupResult(null);
                setElixhauser(null);
                setMce(null);
                setMceFailed(false);
                setMceError("");
                setElixhauserFailed(false);
                setElixhauserError("");
                setHasNoElixhauserDiagnoses(false);
              }}
            >
              <IconRotate size={14} /> Reset
            </button>
          </div>
          <div className="flex w-2/5 items-center justify-end gap-2">
            <button
              type="button"
              className="inline-flex h-9 items-center gap-1 rounded-md border border-fuchsia-300 bg-fuchsia-100 px-3 text-sm text-gray-700 hover:bg-fuchsia-200 disabled:opacity-50"
              disabled={diagnosisCodes.length === 0}
              onClick={() => void openDrgOptions()}
            >
              <IconTable size={14} /> DRG Options
            </button>
            <div className="relative" ref={profilePickerRef}>
              <button
                type="button"
                className="inline-flex h-9 min-w-44 items-center justify-between gap-2 rounded-md border-2 border-blue-300 bg-blue-100 px-2 hover:bg-blue-100"
                onClick={() => setProfilePickerOpen((open) => !open)}
              >
                <span className="flex min-w-0 flex-col items-start leading-tight">
                  <span className="text-[10px] text-slate-500">Patient Profile</span>
                  <span className="max-w-32 truncate text-xs font-medium text-slate-700">
                    {profile.name}
                  </span>
                </span>
                <IconSelector size={16} className="shrink-0 text-slate-400" />
              </button>
              {profilePickerOpen ? (
                <div className="absolute right-0 z-30 mt-1 w-72 overflow-hidden rounded-md border-2 border-blue-300 bg-white shadow-lg">
                  <p className="border-b px-3 py-2 text-xs text-slate-500">
                    Select patient profile...
                  </p>
                  <div className="max-h-64 overflow-auto">
                    {profiles.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                        onClick={() => {
                          setProfileId(item.id);
                          setProfilePickerOpen(false);
                        }}
                      >
                        <IconCheck
                          size={14}
                          className={profile.id === item.id ? "opacity-100" : "opacity-0"}
                        />
                        <span className="min-w-0 flex-1 truncate">{item.name}</span>
                        {item.isDefault ? (
                          <span className="text-[10px] text-slate-400">Default</span>
                        ) : item.isTemporary ? (
                          <span className="text-[10px] text-amber-600">Temporary</span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                  <div className="border-t p-1">
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50"
                      onClick={() => openProfileDialog("create")}
                    >
                      <IconPlus size={14} /> Create profile
                    </button>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50"
                      onClick={() => openProfileDialog("temporary")}
                    >
                      <IconPlus size={14} className="text-amber-600" /> Temporary
                      profile
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 gap-4 overflow-hidden p-2">
        <div className="min-h-0 w-3/5 overflow-auto">
          <div className="flex flex-col gap-2 pb-6">
            {renderCodeTable("ICD-10", diagnosisCodes, false)}
            {renderCodeTable("PCS", procedureCodes, true)}
            {mismatchedCodeSex ? (
              <p className="border-b border-gray-200 px-2 py-1 text-xs font-medium text-red-600">
                * Selected profile is {sexLabel(profile.sex)}, but the claim contains{" "}
                {sexLabel(mismatchedCodeSex)}-restricted Dx and/or PCS code(s).
              </p>
            ) : null}
          </div>
        </div>
        <div className="relative min-h-0 w-2/5 overflow-auto">
          <div className="mb-1 overflow-hidden whitespace-nowrap bg-blue-100 px-2 py-1 text-[11px] text-slate-900">
            <span className="rounded bg-blue-200 px-1.5 py-0.5 font-semibold">
              Profile
            </span>
            <span className="mx-2 text-blue-400">|</span>
            <span className="text-gray-500">Sex:</span> {sexLabel(profile.sex)} ·{" "}
            <span className="text-gray-500">Age:</span> {profile.ageAtAdmission} ·{" "}
            <span className="text-gray-500">Admit Date:</span>{" "}
            {formatEncoderProfileDate(profile.admitDate)} ·{" "}
            <span className="text-gray-500">DSP:</span>{" "}
            {dispositionLabel(dispositions, profile.dischargeStatus)}
          </div>
          <div
            className={group.isPending ? "opacity-60 blur-[2px]" : ""}
          >
            <div className="bg-fuchsia-100 px-1 py-1">
              <h2 className="flex items-center justify-between text-base font-bold">
                <span className="flex items-center gap-2">
                  <span>MS-DRG Results</span>
                  {grouperVersion ? (
                    <span className="text-xs font-normal text-gray-600">
                      v{grouperVersion}
                    </span>
                  ) : null}
                </span>
                {details?.Weights ? (
                  <span className="text-sm font-medium">
                    <span className="text-gray-600">DRG Weight:</span>{" "}
                    <span>{String(details.Weights)}</span>
                  </span>
                ) : null}
              </h2>
              <table className="mt-1 w-full text-sm">
                <tbody>
                  <tr className="border-b border-gray-200">
                    <th className="w-[15%] whitespace-nowrap bg-gray-50 p-1 text-left font-medium text-gray-900">
                      MS-DRG
                    </th>
                    <td className="bg-blue-100 p-1 px-4" colSpan={5}>
                      <div className="flex items-center gap-2">
                        <span>
                          {drgValue
                            ? `${drgValue}${
                                drgDescription ? ` - ${drgDescription}` : ""
                              }`
                            : ""}
                        </span>
                        {drgType ? (
                          <span
                            className={`h-4 rounded border px-1.5 text-[10px] font-medium ${
                              drgType === "MED"
                                ? "border-blue-300 bg-blue-100 text-blue-800"
                                : "border-red-300 bg-red-100 text-red-800"
                            }`}
                          >
                            {drgType}
                          </span>
                        ) : null}
                        {hacStatusMessage ? (
                          <span
                            title={hacStatusMessage}
                            className={`h-4 rounded border px-1.5 text-[10px] font-medium ${
                              hacStatus === "FINAL_DRG_NO_CHANGE"
                                ? "border-green-300 bg-green-100 text-green-800"
                                : "border-red-300 bg-red-100 text-red-800"
                            }`}
                          >
                            HAC
                          </span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <th className="whitespace-nowrap bg-gray-50 p-1 text-left font-medium text-gray-900">
                      Payment
                    </th>
                    <td className="bg-blue-100 p-1 px-4">
                      {details?.DOLLARS ? String(details.DOLLARS) : ""}
                    </td>
                    <th className="whitespace-nowrap bg-gray-50 p-1 text-left font-medium text-gray-900">
                      GMLOS
                    </th>
                    <td className="bg-blue-100 p-1 px-4">
                      {details?.GLOS ? `${String(details.GLOS)} Days` : ""}
                    </td>
                    <th className="whitespace-nowrap bg-gray-50 p-1 text-left font-medium text-gray-900">
                      AMLOS
                    </th>
                    <td className="bg-blue-100 p-1 px-4">
                      {details?.ALOS ? `${String(details.ALOS)} Days` : ""}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <th className="whitespace-nowrap bg-gray-50 p-1 text-left font-medium text-gray-900">
                      MDC
                    </th>
                    <td className="bg-blue-100 p-1 px-4" colSpan={5}>
                      {groupResult
                        ? `${mdc}${mdcDescription ? ` - ${mdcDescription}` : ""}`
                        : ""}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {groupResult ? (
              <>
                <div
                  className={`mt-1 p-1 ${
                    mceChecking
                      ? "bg-blue-300"
                      : mceFailed || mceHasAlerts
                        ? "bg-red-300"
                        : "bg-green-300"
                  }`}
                >
                  <h2 className="mb-1 flex min-h-7 w-full items-center gap-2 pr-1 text-base font-bold">
                    <span className="min-w-0 flex-1">Medicare Code Edit(s)</span>
                    <StatusPill
                      tone={
                        mceChecking
                          ? "blue"
                          : mceFailed || mceHasAlerts
                            ? "red"
                            : "green"
                      }
                    >
                      {mceChecking ? (
                        <>
                          CHECKING
                          <IconLoader2 size={16} className="animate-spin" />
                        </>
                      ) : mceFailed ? (
                        <>
                          FAILED
                          <IconAlertTriangle size={16} />
                        </>
                      ) : mceHasAlerts ? (
                        <>
                          ALERT
                          <IconAlertTriangle size={16} />
                        </>
                      ) : (
                        <>
                          PASSED
                          <IconCheck size={16} stroke={3} />
                        </>
                      )}
                    </StatusPill>
                  </h2>
                  {mceChecking ? (
                    <div className="bg-white p-2 text-sm">
                      Running Medicare Code Editor validation…
                    </div>
                  ) : mceFailed ? (
                    <div className="flex items-center justify-between gap-3 bg-white p-2 text-sm">
                      <span>
                        {mceError || "Medicare Code Editor validation failed."}
                      </span>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs disabled:opacity-50"
                        disabled={mceEdit.isPending}
                        onClick={() => void retryMce()}
                      >
                        <IconRefresh size={14} /> Retry
                      </button>
                    </div>
                  ) : mceHasAlerts ? (
                    <table className="mt-1 w-full text-sm">
                      <tbody>
                        {mceAlerts.map((codeResult) => (
                          <tr
                            key={`${codeResult.role}-${codeResult.value}`}
                            className="border-b border-gray-200"
                          >
                            <th className="w-[15%] bg-gray-50 p-1 text-left align-top font-medium text-gray-900">
                              {codeResult.value}
                            </th>
                            <td className="space-y-2 bg-red-100 p-1 px-4">
                              {codeResult.edits.map((edit, editIndex) => (
                                <div
                                  key={`${edit.index ?? editIndex}-${edit.code ?? ""}`}
                                  className="text-orange-700"
                                  title={edit.reason || undefined}
                                >
                                  {edit.description}
                                  {String(edit.code || "")
                                    .toUpperCase()
                                    .indexOf("AGE_CONFLICT") >= 0 &&
                                  codeResult.ageConflictType
                                    ? `: ${codeResult.ageConflictType}`
                                    : ""}
                                </div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="bg-white p-1 text-sm">
                      No Medicare Code Edit(s) Alerts
                    </div>
                  )}
                </div>
                <div
                  className={`mt-1 p-1 ${
                    hacAlerts.length > 0 ? "bg-red-300" : "bg-green-300"
                  }`}
                >
                  <h2 className="mb-1 flex min-h-7 w-full items-center gap-2 pr-1 text-base font-bold">
                    <span className="min-w-0 flex-1">
                      Hospital Acquired Condition(s)
                    </span>
                    <StatusPill tone={hacAlerts.length > 0 ? "red" : "green"}>
                      {hacAlerts.length > 0 ? (
                        <>
                          ALERT
                          <IconAlertTriangle size={16} />
                        </>
                      ) : (
                        <>
                          PASSED
                          <IconCheck size={16} stroke={3} />
                        </>
                      )}
                    </StatusPill>
                  </h2>
                  {hacAlerts.length > 0 ? (
                    <table className="mt-1 w-full text-sm">
                      <tbody>
                        {hacAlerts.map((alert, index) => (
                          <tr
                            key={`${alert.code}-${index}`}
                            className="border-b border-gray-200"
                          >
                            <th className="w-[15%] bg-gray-50 p-1 text-left align-top font-medium text-gray-900">
                              {alert.code}
                            </th>
                            <td className="bg-red-100 p-1 px-4 text-red-700">
                              {alert.message}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="bg-white p-1 text-sm">
                      No Hospital Acquired Condition(s) reported
                    </div>
                  )}
                </div>
                <div
                  className={`mt-1 p-1 ${
                    elixhauserFailed
                      ? "bg-red-100"
                      : hasElixhauserFindings
                        ? "bg-red-300"
                        : elixhauser
                          ? "bg-green-300"
                          : "bg-fuchsia-100"
                  }`}
                >
                  <h2 className="mb-1 flex min-h-7 w-full items-center gap-2 text-base font-bold">
                    <span>Elixhauser</span>
                    {elix.isPending ? (
                      <IconLoader2 size={16} className="animate-spin" />
                    ) : null}
                    {elixhauser && !hasElixhauserFindings ? (
                      <StatusPill tone="green">
                        PASSED
                        <IconCheck size={16} stroke={3} />
                      </StatusPill>
                    ) : hasElixhauserFindings ? (
                      <StatusPill tone="red">
                        ALERT
                        <IconAlertTriangle size={16} />
                      </StatusPill>
                    ) : null}
                  </h2>
                  {elix.isPending ? (
                    <p className="mt-1 bg-white p-1 text-sm text-gray-600">
                      Running Elixhauser comorbidity analysis…
                    </p>
                  ) : hasNoElixhauserDiagnoses ? (
                    <p className="mt-1 bg-white p-1 text-sm text-gray-600">
                      No secondary diagnoses available for Elixhauser analysis
                    </p>
                  ) : elixhauserFailed ? (
                    <div className="mt-1 flex items-center justify-between gap-3 bg-white p-2 text-sm text-red-700">
                      <span>
                        {elixhauserError ||
                          "Elixhauser analysis could not be completed"}
                      </span>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-800 disabled:opacity-50"
                        disabled={elix.isPending}
                        onClick={() => void retryElixhauser()}
                      >
                        <IconRefresh size={14} /> Retry
                      </button>
                    </div>
                  ) : triggeredMeasures.length > 0 ? (
                    <div className="mt-1 overflow-hidden rounded border bg-white">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-200 text-left text-slate-700">
                          <tr>
                            <th className="p-1 px-3 text-center font-medium">
                              Code
                            </th>
                            <th className="p-1 px-4 font-medium">
                              Elixhauser Comorbidity
                            </th>
                            <th className="p-1 px-3 text-center font-medium">
                              Mortality Impact
                            </th>
                            <th className="p-1 px-3 text-center font-medium">
                              Readmission Impact
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {triggeredMeasures.map((measure, index) => (
                            <tr
                              key={measure.measure || String(index)}
                              className="border-b border-gray-200"
                            >
                              <th className="whitespace-nowrap bg-gray-50 p-1 px-3 font-medium text-gray-900">
                                {(measure.diagnoses || [])
                                  .map((diagnosis) => diagnosis.code)
                                  .filter(Boolean)
                                  .join(", ")}
                              </th>
                              <td className="bg-red-100 p-1 px-4 text-red-700">
                                {measure.description}
                              </td>
                              <td className="p-1 px-3 text-center font-bold text-gray-700">
                                {measure.mortality_impact == null
                                  ? "—"
                                  : String(measure.mortality_impact)}
                              </td>
                              <td className="p-1 px-3 text-center font-bold text-gray-700">
                                {measure.readmission_impact == null
                                  ? "—"
                                  : String(measure.readmission_impact)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : elixhauser ? (
                    <p className="mt-1 bg-white p-1 text-sm">
                      No Elixhauser comorbidities found
                    </p>
                  ) : (
                    <p className="mt-1 bg-white p-1 text-sm text-gray-600">
                      Run grouping to analyze Elixhauser comorbidities
                    </p>
                  )}
                </div>
              </>
            ) : null}
          </div>
          {group.isPending ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="h-10 w-10 animate-spin rounded-full border-4 border-fuchsia-200 border-t-fuchsia-700" />
            </div>
          ) : null}
        </div>
      </div>

      <EncoderDialog
        open={dxOpen}
        title="ICD-10-CM Builder"
        onClose={() => setDxOpen(false)}
        wide
      >
        <div className="h-[70vh]">
          <CmBuilderTab
            onConfirmCode={(payload) => {
              addCode({
                code: payload.code,
                description: payload.description,
                labels: [],
                type: "Dx",
              });
              setDxOpen(false);
            }}
          />
        </div>
      </EncoderDialog>
      <EncoderDialog
        open={pcsOpen}
        title="PCS Builder"
        onClose={() => setPcsOpen(false)}
        wide
      >
        <div className="h-[70vh]">
          <PcsBuilderTab
            onConfirmCode={(payload) => {
              addCode({
                code: payload.code,
                description: payload.description,
                labels: [],
                type: "PCS",
              });
              setPcsOpen(false);
            }}
          />
        </div>
      </EncoderDialog>
      {detailsOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[90vh] w-[min(1400px,96vw)] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
            <div className="flex items-start justify-between gap-4 px-5 pt-4 pb-2">
              <div>
                <h2 className="text-base font-semibold text-slate-900">
                  DRG Options Medicare
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Select a new Principal Diagnosis if supported by documentation,
                  or click Cancel
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  className="h-8 rounded-md border border-slate-300 bg-white px-3 text-sm"
                  onClick={() => setDetailsOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="h-8 rounded-md bg-black px-4 text-sm font-medium text-white disabled:opacity-50"
                  disabled={!draftPdx}
                  onClick={applyDrgOption}
                >
                  OK
                </button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-auto px-3 pb-4">
              {drgOptionsLoading ? (
                <div className="flex items-center justify-center py-16 text-sm text-slate-500">
                  <IconLoader2 size={18} className="mr-2 animate-spin" />
                  Calculating DRG options…
                </div>
              ) : drgOptionRows.length === 0 ? (
                <p className="p-4 text-sm text-slate-500">
                  Add diagnosis codes to begin.
                </p>
              ) : (
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="bg-fuchsia-100 text-[11px] font-semibold uppercase tracking-wide text-fuchsia-950">
                      <th className="px-2 py-2">PDX</th>
                      <th className="px-2 py-2">PPX</th>
                      <th className="px-2 py-2">DRG</th>
                      <th className="px-2 py-2">Weight</th>
                      <th className="px-2 py-2">ALOS</th>
                      <th className="px-2 py-2">OLOS</th>
                      <th className="px-2 py-2">Reimb $</th>
                      <th className="px-2 py-2">PDX Description</th>
                      <th className="px-2 py-2">DRG Description</th>
                      <th className="px-2 py-2">MC EDITS</th>
                      <th className="px-2 py-2">HAC</th>
                      <th className="px-2 py-2">ELIX</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drgOptionRows.map((row) => {
                      const selected = draftPdx === row.pdx;
                      return (
                        <tr
                          key={row.pdx}
                          onClick={() => setDraftPdx(row.pdx)}
                          className={cn(
                            "cursor-pointer border-b border-slate-100 align-top hover:bg-fuchsia-50/60",
                            selected && "bg-fuchsia-50"
                          )}
                        >
                          <td className="whitespace-nowrap px-2 py-2 font-mono">
                            <span className="inline-flex items-center gap-1">
                              {row.pdx}
                              {selected ? (
                                <span className="rounded-sm bg-[#1d4ed8] px-1 py-px text-[10px] font-semibold text-white">
                                  PDX
                                </span>
                              ) : null}
                            </span>
                          </td>
                          <td className="px-2 py-2 font-mono">{row.ppx}</td>
                          <td className="px-2 py-2">{row.drg}</td>
                          <td className="px-2 py-2">{row.weight}</td>
                          <td className="px-2 py-2">{row.alos}</td>
                          <td className="px-2 py-2">{row.olos}</td>
                          <td className="px-2 py-2">{row.reimb}</td>
                          <td className="max-w-[14rem] truncate px-2 py-2" title={row.pdxDescription}>
                            {row.pdxDescription}
                          </td>
                          <td className="max-w-[16rem] px-2 py-2">
                            <span className="inline-flex items-center gap-1">
                              <span className="truncate" title={row.drgDescription}>
                                {row.drgDescription}
                              </span>
                              {row.drgType ? (
                                <span
                                  className={`h-4 shrink-0 rounded border px-1.5 text-[10px] font-medium ${
                                    row.drgType === "MED"
                                      ? "border-blue-300 bg-blue-100 text-blue-800"
                                      : "border-red-300 bg-red-100 text-red-800"
                                  }`}
                                >
                                  {row.drgType}
                                </span>
                              ) : null}
                            </span>
                          </td>
                          <td className="px-2 py-2">
                            {row.mcEdits === "passed" ? (
                              <PassedChip />
                            ) : (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                                {row.mcEdits === "failed" ? "FAILED" : "ALERT"}
                                <IconAlertTriangle size={12} />
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-2">
                            {row.hac === "passed" ? (
                              <PassedChip />
                            ) : (
                              <span className="inline-flex items-center gap-0.5 rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                                ALERT
                                <IconAlertTriangle size={12} />
                              </span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-2 py-2 text-xs text-slate-700">
                            {row.elix === "PASSED" ? <PassedChip /> : row.elix}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ) : null}
      <EncoderDialog
        open={Boolean(profileDialog)}
        title={profileDialog === "temporary" ? "Temporary profile" : "Create profile"}
        onClose={() => setProfileDialog(null)}
      >
        <div className="space-y-3">
          <label className="block text-xs text-slate-500">
            Profile name
            <input
              value={draftName}
              onChange={(event) => setDraftName(event.target.value)}
              className="mt-1 h-10 w-full rounded-md border px-3 text-sm"
              placeholder="e.g. Medicare inpatient"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs text-slate-500">
              Sex
              <NativeSelect
                value={draftProfile.sex}
                onChange={(value) =>
                  setDraftProfile((current) => ({
                    ...current,
                    sex: value as MsDrgSex,
                  }))
                }
                className="mt-1 h-10 w-full"
                options={sexOptions.map((item) => ({
                  value: item.value,
                  label: item.label,
                }))}
              />
            </label>
            <label className="block text-xs text-slate-500">
              Age at admission
              <input
                type="number"
                min={0}
                max={150}
                value={draftProfile.ageAtAdmission}
                onChange={(event) =>
                  setDraftProfile((current) => ({
                    ...current,
                    ageAtAdmission: Number(event.target.value) || 0,
                  }))
                }
                className="mt-1 h-10 w-full rounded-md border px-3 text-sm"
              />
            </label>
            <label className="block text-xs text-slate-500">
              Admit date
              <input
                type="date"
                value={draftProfile.admitDate}
                onChange={(event) =>
                  setDraftProfile((current) => ({
                    ...current,
                    admitDate: event.target.value,
                  }))
                }
                className="mt-1 h-10 w-full rounded-md border px-3 text-sm"
              />
            </label>
            <label className="block text-xs text-slate-500">
              Discharge date
              <input
                type="date"
                value={draftProfile.dischargeDate}
                onChange={(event) =>
                  setDraftProfile((current) => ({
                    ...current,
                    dischargeDate: event.target.value,
                  }))
                }
                className="mt-1 h-10 w-full rounded-md border px-3 text-sm"
              />
            </label>
          </div>
          <label className="block text-xs text-slate-500">
            Discharge status
            <NativeSelect
              value={draftProfile.dischargeStatus}
              onChange={(value) =>
                setDraftProfile((current) => ({
                  ...current,
                  dischargeStatus: value,
                }))
              }
              className="mt-1 h-10 w-full"
              options={dispositions.map((item) => ({
                value: (item.code || item.value).padStart(2, "0"),
                label: `${(item.code || item.value).padStart(2, "0")} ${item.label}`,
              }))}
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="h-9 rounded-md border px-3 text-sm"
              onClick={() => setProfileDialog(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex h-9 items-center gap-1 rounded-md bg-slate-900 px-3 text-sm text-white"
              onClick={saveProfile}
            >
              Save
            </button>
          </div>
        </div>
      </EncoderDialog>
    </section>
  );
};

export default MsDrgGrouperTab;
